import { useEffect, useRef } from 'react';

interface LiveRegionProps {
  message: string;
  level?: 'polite' | 'assertive';
  clearAfterDelay?: number;
}

export const LiveRegion = ({ 
  message, 
  level = 'polite', 
  clearAfterDelay = 3000 
}: LiveRegionProps) => {
  const regionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message && regionRef.current) {
      regionRef.current.textContent = message;
      
      if (clearAfterDelay > 0) {
        const timer = setTimeout(() => {
          if (regionRef.current) {
            regionRef.current.textContent = '';
          }
        }, clearAfterDelay);
        
        return () => clearTimeout(timer);
      }
    }
  }, [message, clearAfterDelay]);

  return (
    <div
      ref={regionRef}
      aria-live={level}
      aria-atomic="true"
      className="sr-only"
      role="status"
    />
  );
};

// Hook for managing live region announcements
export const useLiveRegion = () => {
  const announce = (message: string, level: 'polite' | 'assertive' = 'polite') => {
    const region = document.getElementById('live-region-announcer');
    if (region) {
      region.setAttribute('aria-live', level);
      region.textContent = message;
      
      // Clear after announcement
      setTimeout(() => {
        region.textContent = '';
      }, 1000);
    }
  };

  return { announce };
};

// Global live region component to be placed once in the app
export const GlobalLiveRegion = () => (
  <div
    id="live-region-announcer"
    aria-live="polite"
    aria-atomic="true"
    className="sr-only"
    role="status"
  />
);